import { useEffect, useRef, useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6urtb5t4s7j0tv5i8sg';

export default function EmailCapture() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.2 }
    );
    itemsRef.current.forEach((el) => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);

    try {
      const body = new URLSearchParams();
      body.append('email', email);
      body.append('_recipient', 'hello@urbanblings.com');

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="section-early-access"
      data-section="Early Access"
      ref={sectionRef}
      className="relative w-full py-36 md:py-48 overflow-hidden"
      style={{ background: '#0A0A0A', borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      {/* Anchor target for href="#early-access" links */}
      <span id="early-access" className="absolute -top-20" aria-hidden="true" />

      {/* Top gradient line */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)' }}
      />

      {/* Background text */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
        <span
          className="font-display font-black uppercase text-white select-none"
          style={{ fontSize: 'clamp(60px, 14vw, 190px)', opacity: 0.022, letterSpacing: '0.06em', whiteSpace: 'nowrap' }}
        >
          ACCESS
        </span>
      </div>

      <div className="relative z-10 w-full px-8 md:px-16">
        <div className="max-w-xl mx-auto text-center">

          {/* Label */}
          <div
            ref={(el) => { itemsRef.current[0] = el; }}
            className="reveal flex items-center justify-center gap-4 mb-12"
          >
            <div className="w-10 h-px bg-white/20" />
            <span className="font-display text-[9px] tracking-[0.5em] uppercase text-white/35">
              Pre-Launch
            </span>
            <div className="w-10 h-px bg-white/20" />
          </div>

          {/* Headline */}
          <div
            ref={(el) => { itemsRef.current[1] = el; }}
            className="reveal reveal-delay-1 mb-5"
          >
            <h2
              className="font-display font-black uppercase text-white leading-tight"
              style={{ fontSize: 'clamp(30px, 4.5vw, 52px)', letterSpacing: '-0.015em' }}
            >
              Enter Early Access
            </h2>
          </div>

          {/* Subtext */}
          <div
            ref={(el) => { itemsRef.current[2] = el; }}
            className="reveal reveal-delay-2 mb-14"
          >
            <p className="font-display text-xs font-light text-white/35 tracking-[0.25em] uppercase">
              First drop.&nbsp;&nbsp;Limited pieces.&nbsp;&nbsp;No restocks.
            </p>
          </div>

          {/* Form */}
          <div
            ref={(el) => { itemsRef.current[3] = el; }}
            className="reveal reveal-delay-3"
          >
            {submitted ? (
              <div className="flex flex-col items-center gap-5">
                <div
                  className="w-12 h-12 flex items-center justify-center"
                  style={{ border: '1px solid rgba(212,175,55,0.4)' }}
                >
                  <i className="ri-check-line text-xl" style={{ color: '#D4AF37' }} />
                </div>
                <p className="font-display text-sm tracking-[0.25em] uppercase text-white/50">
                  You&apos;re on the list. Stay close.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row items-stretch gap-2"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="flex-1 bg-transparent border border-white/15 text-white text-sm font-display placeholder-white/25 px-6 py-4 focus:outline-none focus:border-white/40 transition-colors duration-300"
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="font-display font-bold text-[10px] uppercase tracking-[0.3em] px-8 py-4 text-black transition-all duration-300 hover:opacity-90 whitespace-nowrap cursor-pointer disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #D4D4D4 0%, #F0F0F0 40%, #C8C8C8 70%, #D4AF37 100%)' }}
                >
                  {loading ? 'Joining...' : 'Join Now'}
                </button>
              </form>
            )}
            {error && (
              <p className="mt-4 text-xs text-red-400/70 font-display tracking-wider">{error}</p>
            )}
          </div>

        </div>
      </div>

      {/* Bottom gradient line */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2/3 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)' }}
      />
    </section>
  );
}
